import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryKeys } from '@/api/query-client';
import type { components, paths } from '@/api/generated/schema';

export type TemplateAdminListItem =
  components['schemas']['PixlideApplicationFeaturesTemplatesTemplateAdminListItem'];

/** Tous les templates vidéo, brouillons, publiés et archivés (vue admin). */
export function useTemplatesAdmin() {
  return useQuery({
    queryKey: queryKeys.templatesAdmin,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/templates');

      return data!;
    },
  });
}

export type TemplateAnimationInspection =
  components['schemas']['PixlideApplicationFeaturesTemplatesTemplateAnimationInspectionResponse'];

/** Extension du nom de fichier, point compris (`.json`), ou chaîne vide. */
function extensionOf(file: File): string {
  return file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
}

/** Dépôt direct sur le stockage objet via une URL présignée. */
async function putToStorage(
  ticket: { uploadUrl: string; requiredHeaders: Record<string, string> },
  file: File,
  label: string,
): Promise<void> {
  const response = await fetch(ticket.uploadUrl, { method: 'PUT', headers: ticket.requiredHeaders, body: file });

  if (!response.ok) {
    throw new Error(`Échec du dépôt ${label} (${response.status}).`);
  }
}

async function confirmAnimationSource(templateId: string): Promise<TemplateAnimationInspection> {
  // La confirmation analyse le Lottie et crée les champs détectés (calques slot-…) :
  // son résultat est renvoyé pour que l'admin voie immédiatement ce qui a été reconnu.
  const { data } = await api.POST('/api/v1/admin/templates/{templateId}/confirm-animation-source', {
    params: { path: { templateId } },
    // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
    body: {},
  });

  return data!;
}

async function confirmThumbnail(templateId: string): Promise<void> {
  await api.POST('/api/v1/admin/templates/{templateId}/confirm-thumbnail', {
    params: { path: { templateId } },
    // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
    body: {},
  });
}

/** Étapes de la création, annoncées à l'écran : le dépôt de 10 Mo peut prendre du temps. */
export type CreateTemplateStep = 'declaring' | 'uploading' | 'analysing' | 'finishing';

type CreateTemplateBody = NonNullable<
  paths['/api/v1/admin/templates']['post']['requestBody']
>['content']['application/json'];

/**
 * Crée un template vidéo et dépose ses deux fichiers (animation Lottie + vignette), même
 * motif en trois temps que `useCreateVideoOverlayTemplate` : la commande renvoie deux URL
 * présignées, les fichiers sont déposés DIRECTEMENT sur le stockage objet, puis chaque
 * upload est confirmé séparément.
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      animationSource,
      thumbnail,
      onStep,
      ...fields
    }: Omit<
      CreateTemplateBody,
      | 'animationSourceFileExtension'
      | 'animationSourceSizeBytes'
      | 'thumbnailFileExtension'
      | 'thumbnailContentType'
      | 'thumbnailSizeBytes'
    > & {
      animationSource: File;
      thumbnail: File;
      onStep?: (step: CreateTemplateStep) => void;
    }) => {
      onStep?.('declaring');

      const { data: tickets } = await api.POST('/api/v1/admin/templates', {
        body: {
          ...fields,
          animationSourceFileExtension: extensionOf(animationSource),
          animationSourceSizeBytes: animationSource.size,
          thumbnailFileExtension: extensionOf(thumbnail),
          thumbnailContentType: thumbnail.type,
          thumbnailSizeBytes: thumbnail.size,
        },
      });

      if (!tickets) {
        throw new Error("Impossible d'obtenir les URL de téléversement du template.");
      }

      // Les deux dépôts en parallèle : ils visent deux objets indépendants du stockage.
      onStep?.('uploading');
      await Promise.all([
        putToStorage(tickets.animationSource, animationSource, "de l'animation"),
        putToStorage(tickets.thumbnail, thumbnail, 'de la vignette'),
      ]);

      onStep?.('analysing');
      const inspection = await confirmAnimationSource(tickets.templateId);

      onStep?.('finishing');
      await confirmThumbnail(tickets.templateId);

      return { tickets, inspection };
    },

    // onSettled et non onSuccess : un échec APRÈS la déclaration laisse un brouillon dont un
    // fichier reste à redéposer. Il doit apparaître dans la liste pour pouvoir être repris.
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templatesAdmin });
    },
  });
}

/**
 * Redépose l'animation ou la vignette d'un BROUILLON : nouvelle URL présignée, dépôt direct,
 * puis confirmation. Pour l'animation, renvoie la nouvelle analyse (champs détectés, alertes).
 */
export function useReplaceTemplateFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      kind,
      file,
    }: {
      templateId: string;
      kind: 'animation' | 'thumbnail';
      file: File;
    }): Promise<TemplateAnimationInspection | null> => {
      const isAnimation = kind === 'animation';

      const { data: ticket } = await api.POST('/api/v1/admin/templates/{templateId}/files', {
        params: { path: { templateId } },
        body: {
          file: isAnimation ? 0 : 1,
          fileExtension: extensionOf(file),
          contentType: isAnimation ? 'application/json' : file.type,
          sizeBytes: file.size,
        },
      });

      if (!ticket) {
        throw new Error("Impossible d'obtenir l'URL de téléversement.");
      }

      await putToStorage(ticket, file, isAnimation ? "de l'animation" : 'de la vignette');

      if (isAnimation) {
        return confirmAnimationSource(templateId);
      }

      await confirmThumbnail(templateId);

      return null;
    },

    onSettled: async (_data, _error, { templateId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templatesAdmin });
      await queryClient.invalidateQueries({ queryKey: queryKeys.templatePreview(templateId) });
    },
  });
}

export type TemplatePreview = components['schemas']['PixlideApplicationFeaturesTemplatesTemplatePreviewResponse'];

/**
 * Aperçu rendu par le moteur de production, à une fraction du déroulé.
 *
 * Mis en cache par position (`staleTime` infini) : le contenu d'origine d'une animation ne
 * change qu'à un redépôt, qui invalide explicitement ces entrées. Revenir sur une position
 * déjà vue ne relance donc pas de rendu côté serveur, où chaque appel coûte du CPU et compte
 * dans la limite de débit.
 */
export function useTemplatePreview(templateId: string, progress: number, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.templatePreview(templateId), progress],

    queryFn: async ({ signal }) => {
      const { data } = await api.GET('/api/v1/admin/templates/{templateId}/preview', {
        params: { path: { templateId }, query: { progress } },
        signal,
      });

      return data!;
    },

    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

type UpdateTemplateBody = NonNullable<
  paths['/api/v1/admin/templates/{templateId}']['put']['requestBody']
>['content']['application/json'];

/**
 * Modifie les informations d'un template : nom, description, catégorie, durée.
 *
 * Ni le format ni le fichier d'animation : ils forment le contrat des projets clients déjà
 * créés à partir de ce template, et les faire bouger reviendrait à changer une vidéo sous les
 * pieds de son propriétaire.
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, ...fields }: UpdateTemplateBody & { templateId: string }) => {
      await api.PUT('/api/v1/admin/templates/{templateId}', {
        params: { path: { templateId } },
        body: { templateId, ...fields },
      });
    },

    // Invalider la liste suffit à rafraîchir AUSSI le journal : sa clé commence par celle de
    // la liste (`['admin','templates', id, 'audit']`), et React Query invalide par préfixe.
    // La nouvelle ligne « Modifié » apparaît donc sans invalidation supplémentaire.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templatesAdmin });
    },
  });
}

/**
 * Rend un champ détecté modifiable par le client, ou le fige.
 *
 * Figer ne supprime pas le champ : le calque garde sa valeur d'origine au rendu, et
 * l'opération est réversible — y compris sur un template déjà publié.
 */
export function useSetTemplateFieldEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      key,
      isEnabled,
    }: {
      templateId: string;
      key: string;
      isEnabled: boolean;
    }) => {
      await api.PUT('/api/v1/admin/templates/{templateId}/fields/{key}/enabled', {
        params: { path: { templateId, key } },
        body: { templateId, key, isEnabled },
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templatesAdmin });
    },
  });
}

export type TemplateAuditEntry =
  components['schemas']['PixlideApplicationFeaturesTemplatesTemplateAuditEntryResponse'];

/**
 * Journal d'un template : qui l'a créé, modifié, publié, archivé, et quand.
 *
 * Chargé À LA DEMANDE (`enabled`) et non avec la liste : l'historique n'intéresse qu'au
 * moment où on l'ouvre, et le réclamer pour chaque carte ferait autant de requêtes que de
 * templates affichés.
 */
export function useTemplateAuditLog(templateId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.templateAudit(templateId),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/templates/{templateId}/audit', {
        params: { path: { templateId } },
      });

      return data!;
    },

    enabled,
  });
}

type AddTemplateFieldBody = components['schemas']['PixlideApplicationFeaturesTemplatesAddTemplateFieldCommand'];

/**
 * Ajout manuel d'un champ, pour les cas que la détection automatique ne couvre pas. La clé
 * correspond à l'identifiant du slot (`slot-text-prix` → `prix`, `slot-color-fond` → `fond_color`).
 */
export function useAddTemplateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, ...field }: AddTemplateFieldBody & { templateId: string }) => {
      await api.POST('/api/v1/admin/templates/{templateId}/fields', {
        params: { path: { templateId } },
        body: field,
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templatesAdmin });
    },
  });
}

function useTemplateLifecycleMutation(action: 'publish' | 'archive') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      await api.POST(
        action === 'publish'
          ? '/api/v1/admin/templates/{templateId}/publish'
          : '/api/v1/admin/templates/{templateId}/archive',
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        { params: { path: { templateId } }, body: {} },
      );
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templatesAdmin });
    },
  });
}

export function usePublishTemplate() {
  return useTemplateLifecycleMutation('publish');
}

export function useArchiveTemplate() {
  return useTemplateLifecycleMutation('archive');
}

export function useSetTemplatePremium() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, isPremium }: { templateId: string; isPremium: boolean }) => {
      await api.PUT('/api/v1/admin/templates/{templateId}/premium', {
        params: { path: { templateId } },
        body: { isPremium },
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.templatesAdmin });
    },
  });
}
